import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FaChevronDown, FaChevronRight } from "react-icons/fa6";
import toast from "react-hot-toast";

import { getOrganizationList } from "../../../../services/admin/organization";
import {
  getReviewLocations,
  getReviewCaseManagers,
  startReviewSession,
  setReviewSessionState,
  getApiErrorMessage,
  getSessionConflict,
  SESSION_CONFLICT,
} from "../../../../services/case-manager/readingReviewSession";
import { getUserIdentity } from "../../../../utils/authorization";
import { useOnClickOutside } from "../../../../hooks/useClickOutside";
import selectIllustration from "../../../../assets/images/reading-review/select-case-manager.png";
import { PENDING_SELECTION_KEY, switchAccountForReview } from "./switchAccountForReview";
import { formatTime } from "./format";
import { SessionInUseDialog } from "./SessionDialogs";
import styles from "./ReadingReviewSession.module.css";

const readPendingSelection = () => {
  try {
    const pending = JSON.parse(sessionStorage.getItem(PENDING_SELECTION_KEY) || "null");
    sessionStorage.removeItem(PENDING_SELECTION_KEY);
    return pending;
  } catch {
    return null;
  }
};

// Account › locations picker, laid out like the header's Switch Account /
// Location menu: one account at a time (radio-like), each of its locations
// with a checkbox so several can be reviewed together.
const AccountLocationPicker = ({
  accounts,
  accountId,
  locationIds,
  onChange,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(accountId);
  const ref = useOnClickOutside(() => setOpen(false));

  const { data: expandedLocations = [], isLoading } = useQuery(
    ["reading-review", "locations", expandedId],
    () => getReviewLocations(expandedId),
    { enabled: open && !!expandedId, staleTime: 5 * 60 * 1000 }
  );
  const { data: selectedLocations = [] } = useQuery(
    ["reading-review", "locations", accountId],
    () => getReviewLocations(accountId),
    { enabled: !!accountId, staleTime: 5 * 60 * 1000 }
  );

  const account = accounts.find((a) => String(a.id) === String(accountId));
  const chosen = selectedLocations.filter((loc) => locationIds.includes(String(loc.id)));
  const summary =
    chosen.length === 0
      ? null
      : chosen.length === 1
        ? chosen[0].name
        : `${chosen.length} locations`;

  const toggleLocation = (locId) => {
    const id = String(locId);
    // Locations from another account replace the current selection.
    if (String(expandedId) !== String(accountId)) {
      onChange(String(expandedId), [id]);
      return;
    }
    onChange(
      accountId,
      locationIds.includes(id) ? locationIds.filter((x) => x !== id) : [...locationIds, id]
    );
  };

  return (
    <div className={styles.picker} ref={ref}>
      <button
        type="button"
        className={styles.pickerTrigger}
        onClick={() => {
          setExpandedId(accountId);
          setOpen((o) => !o);
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.pickerValue}>
          {account ? (
            <>
              {account.name}
              {summary ? ` · ${summary}` : <span className={styles.pickerPlaceholder}> · Select locations</span>}
            </>
          ) : (
            <span className={styles.pickerPlaceholder}>Account</span>
          )}
        </span>
        <FaChevronDown size={11} />
      </button>

      {open && (
        <div className={styles.pickerMenu} role="listbox" aria-multiselectable="true">
          {accounts.map((acc) => {
            const isExpanded = String(acc.id) === String(expandedId);
            return (
              <div key={acc.id}>
                <button
                  type="button"
                  className={`${styles.accountRow} ${String(acc.id) === String(accountId) ? styles.accountRowActive : ""}`}
                  onClick={() => setExpandedId(isExpanded ? null : acc.id)}
                >
                  {acc.name}
                  {isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                </button>
                {isExpanded &&
                  (isLoading ? (
                    <div className={styles.menuNote}>Loading locations…</div>
                  ) : expandedLocations.length === 0 ? (
                    <div className={styles.menuNote}>No locations you can review.</div>
                  ) : (
                    expandedLocations.map((loc) => {
                      const checked =
                        String(acc.id) === String(accountId) && locationIds.includes(String(loc.id));
                      return (
                        <label key={loc.id} className={styles.locationRow}>
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={checked}
                            onChange={() => toggleLocation(loc.id)}
                          />
                          {loc.name}
                        </label>
                      );
                    })
                  ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const caseManagerStatus = (cm) => {
  const session = cm.session;
  if (!session) return null;
  if (session.status === "paused") return "paused";
  if (session.status === "active") return session.is_mine ? "mine" : "locked";
  return null;
};

const CaseManagerPicker = ({ caseManagers, value, onChange, disabled, loading }) => {
  const [open, setOpen] = useState(false);
  const ref = useOnClickOutside(() => setOpen(false));
  const selected = caseManagers.find((cm) => String(cm.id) === String(value));

  return (
    <div className={styles.picker} ref={ref}>
      <button
        type="button"
        className={styles.pickerTrigger}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.pickerValue}>
          {loading ? (
            <span className={styles.pickerPlaceholder}>Loading…</span>
          ) : selected ? (
            selected.name
          ) : (
            <span className={styles.pickerPlaceholder}>Care manager</span>
          )}
        </span>
        <FaChevronDown size={11} />
      </button>

      {open && (
        <div className={styles.pickerMenu} role="listbox">
          {caseManagers.length === 0 ? (
            <div className={styles.menuNote} style={{ paddingLeft: 8 }}>
              No care managers in these locations.
            </div>
          ) : (
            caseManagers.map((cm) => {
              const status = caseManagerStatus(cm);
              const locked = status === "locked";
              return (
                <button
                  key={cm.id}
                  type="button"
                  role="option"
                  aria-selected={String(cm.id) === String(value)}
                  className={`${styles.cmOption} ${String(cm.id) === String(value) ? styles.cmOptionActive : ""}`}
                  onClick={() => {
                    onChange(String(cm.id));
                    setOpen(false);
                  }}
                >
                  <span className={styles.cmName}>
                    {cm.name}
                    {status === "paused" && <span className={styles.cmTag}>Paused</span>}
                  </span>
                  <span className={styles.cmMeta}>
                    {locked
                      ? `In use by ${cm.session.locked_by?.name || "another reviewer"}${
                          cm.session.locked_since ? ` since ${formatTime(cm.session.locked_since)}` : ""
                        }`
                      : `${cm.pending_patient_count} of ${cm.patient_count} Patients to review`}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// SCRUM-845: account/locations + case manager, then Next routes on that case
// manager's open session (API #2 `session`): none → start a new one; paused
// → "Continue paused Session?"; active and mine → continue straight away;
// active and someone else's → "Session Already in Use" popup, staying here
// (also when starting/continuing comes back 409 session_locked).
const SessionSelect = ({ onEnterSession, onPausedSession }) => {
  const queryClient = useQueryClient();
  const identity = getUserIdentity();
  const currentAccId = sessionStorage.getItem("acc_id");
  const currentLoc = JSON.parse(sessionStorage.getItem("current_loc") || "null");

  const [pending] = useState(readPendingSelection);
  const [accountId, setAccountId] = useState(pending?.accId || currentAccId);
  const [locationIds, setLocationIds] = useState(pending?.locationIds || []);
  const [caseManagerId, setCaseManagerId] = useState(pending?.caseManagerId || null);
  const [submitting, setSubmitting] = useState(false);
  const [inUse, setInUse] = useState(null); // {caseManagerName, lockedBy}

  // Super Users / TOCA Admins see every account, like the header switcher
  // (same query key, so it shares the header's cached list).
  const { data: organizations = [] } = useQuery(["organizations-list"], getOrganizationList, {
    enabled: !!identity?.hasCrossAccountAccess,
    staleTime: 5 * 60 * 1000,
  });
  const accounts = useMemo(() => {
    const current = {
      id: currentAccId,
      name: sessionStorage.getItem("acc_name") || "Current account",
      schema_name: sessionStorage.getItem("schema_name"),
    };
    if (!identity?.hasCrossAccountAccess || organizations.length === 0) return [current];
    return organizations;
  }, [identity?.hasCrossAccountAccess, organizations, currentAccId]);

  // Default locations: the header's current location when it's reviewable,
  // otherwise the first reviewable one.
  const { data: accountLocations = [] } = useQuery(
    ["reading-review", "locations", accountId],
    () => getReviewLocations(accountId),
    { enabled: !!accountId, staleTime: 5 * 60 * 1000 }
  );
  useEffect(() => {
    if (locationIds.length > 0 || accountLocations.length === 0) return;
    const preferred = accountLocations.find((loc) => String(loc.id) === String(currentLoc?.id));
    setLocationIds([String((preferred || accountLocations[0]).id)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountLocations]);

  const sortedLocationIds = useMemo(() => [...locationIds].sort(), [locationIds]);
  const {
    data: caseManagers = [],
    isFetching: cmLoading,
    refetch: refetchCaseManagers,
  } = useQuery(
    ["reading-review", "case-managers", accountId, sortedLocationIds],
    () => getReviewCaseManagers(accountId, sortedLocationIds),
    {
      enabled: !!accountId && sortedLocationIds.length > 0,
      staleTime: 0,
      onError: (error) => toast.error(getApiErrorMessage(error, "Could not load care managers.")),
    }
  );

  // Preselect the reviewer's own entry (is_me) unless it's in use, keep a
  // choice that's still in the list.
  useEffect(() => {
    if (caseManagers.length === 0) return;
    const stillThere = caseManagers.find((cm) => String(cm.id) === String(caseManagerId));
    if (stillThere) return;
    const me = caseManagers.find((cm) => cm.is_me && caseManagerStatus(cm) !== "locked");
    setCaseManagerId(me ? String(me.id) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseManagers]);

  const selectedCm = caseManagers.find((cm) => String(cm.id) === String(caseManagerId));
  const canProceed = !!selectedCm && locationIds.length > 0 && !submitting;

  const handleSelection = (nextAccountId, nextLocationIds) => {
    if (String(nextAccountId) !== String(accountId)) setCaseManagerId(null);
    setAccountId(nextAccountId);
    setLocationIds(nextLocationIds);
  };

  const handleNext = async () => {
    if (!canProceed) return;
    if (caseManagerStatus(selectedCm) === "locked") {
      setInUse({ caseManagerName: selectedCm.name, lockedBy: selectedCm.session.locked_by?.name });
      return;
    }
    const locIds = locationIds.map(Number);
    setSubmitting(true);
    try {
      // Another account: make it the app's current one first (reloads back
      // here with this selection restored).
      if (String(accountId) !== String(currentAccId)) {
        const account = accounts.find((a) => String(a.id) === String(accountId));
        await switchAccountForReview(account, locationIds, queryClient, caseManagerId);
        return;
      }

      const status = caseManagerStatus(selectedCm);
      if (status === "paused") {
        onPausedSession({ session: selectedCm.session, caseManager: selectedCm, locationIds: locIds });
        return;
      }
      const session =
        status === "mine"
          ? await setReviewSessionState(accountId, selectedCm.session.id, "continue")
          : await startReviewSession(accountId, { caseManagerId: selectedCm.id, locationIds: locIds });
      onEnterSession(session, locIds);
    } catch (error) {
      // Someone else took the session between loading the list and Next.
      if (getSessionConflict(error)?.code === SESSION_CONFLICT.LOCKED) {
        setInUse({ caseManagerName: selectedCm.name, lockedBy: getSessionConflict(error).locked_by?.name });
      } else {
        toast.error(error?.response ? getApiErrorMessage(error) : error?.message || "Something went wrong.");
      }
      refetchCaseManagers();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.centered}>
      <img src={selectIllustration} alt="" className={styles.illustration} />
      <h3 className={styles.heading}>Select Account &amp; case manager</h3>
      <p className={styles.subheading}>for which you want to review readings?</p>

      <div className={styles.form}>
        <AccountLocationPicker
          accounts={accounts}
          accountId={accountId}
          locationIds={locationIds}
          onChange={handleSelection}
          disabled={submitting}
        />
        <CaseManagerPicker
          caseManagers={caseManagers}
          value={caseManagerId}
          onChange={setCaseManagerId}
          loading={cmLoading && caseManagers.length === 0}
          disabled={submitting || locationIds.length === 0}
        />
        <button
          type="button"
          className={`${styles.primaryBtn} ${styles.formNext}`}
          onClick={handleNext}
          disabled={!canProceed}
        >
          {submitting ? "Please wait…" : "Next"}
        </button>
      </div>

      <SessionInUseDialog
        open={!!inUse}
        caseManagerName={inUse?.caseManagerName}
        lockedBy={inUse?.lockedBy}
        onOk={() => setInUse(null)}
      />
    </div>
  );
};

export default SessionSelect;
